import {
  bufferToBase64UrlEncoded,
  createRandomString,
  encode,
  sha256,
} from './ServiceAuthUtils';
import axios from 'axios';
import qs from 'qs';
import { decode } from 'jsonwebtoken';

// Module-level promise to coordinate concurrent refresh attempts
// When one getAccessToken() is refreshing, others will wait for the same promise
let refreshInProgress = null;

class ServiceAuthClient {
  constructor() {
    this.serviceauthRedirectUri =
      process.env.REACT_APP_SERVICEAUTH_REDIRECT_URI;
    this.serviceauthOauthUrl = process.env.REACT_APP_SERVICEAUTH_OAUTH_URL;
    this.serviceauthTokenUrl = process.env.REACT_APP_SERVICEAUTH_TOKEN_URL;
    this.serviceauthTokenRefreshUrl =
      process.env.REACT_APP_SERVICEAUTH_TOKEN_REFRESH_URL;

    this.scopes = [
      'https://api.myasuplat-dpl.asu.edu/scopes/principal/read:self',
      'https://api.myasuplat-dpl.asu.edu/scopes/person/read:self',
      process.env.REACT_APP_SERVICEAUTH_APP_SCOPE,
    ];

    this.serviceauthId =
      process.env.REACT_APP_SERVICEAUTH_OAUTH_PUBLIC_CLIENT_ID;
    this.serviceauthSecret = 'serviceauth-public-agent';

    this.SS_SA_CODE_VERIFIER = 'pat.serviceauth.codeVerifier';
    this.SS_SA_STATE = 'pat.serviceauth.state';
    this.SS_RETURN = 'pat.serviceauth.return';
    this.SS_REDIRECT_IN_PROGRESS = 'pat.serviceauth.redirecting';
    this.SS_JWT_TOKEN = 'pat.jwt.token';
    this.SA_JWT_TOKEN = 'serviceauth.jwt.token';
    //this.PAT_JWT_TOKEN = "pat.app.jwt.token";
    this.SS_JWT_REFRESH_TOKEN = 'pat.jwt.refresh.token';
    this.SS_JWT_EXPIRATION = 'pat.jwt.expiration';
    this.SS_SESSION_EXPIRATION = 'pat.session.expiration'; // ServiceAuth refresh token expiration
    this.TOKEN_EXPIRES_IN_SECONDS = 540;
    this.USER_EMPLID = 'emplid';
    this.USER_ROLE = 'role';
    this.USER_COLLEGE = 'college';
    this.USER_DEPARTMENT = 'department';
    this.USER_DETAILS = 'details';
    this.USER_PERMISSIONS = 'permissions';
    this.USER_EMAIL_PREFERENCE = 'email-preference';

    // this.DPL_BASE_URL = process.env.REACT_APP_DPL_BASE_URL;
  }

  async redirectToServiceauth() {
    // Check localStorage flag - this persists across page navigations
    const redirecting = localStorage.getItem(this.SS_REDIRECT_IN_PROGRESS);
    const redirectTimestamp = localStorage.getItem(
      'pat.serviceauth.redirect.timestamp'
    );

    if (redirecting === 'true') {
      // Check if this redirect flag is stale (older than 30 seconds)
      // A legitimate redirect should complete in < 5 seconds
      if (redirectTimestamp) {
        const flagAge = Date.now() - parseInt(redirectTimestamp, 10);
        const STALE_THRESHOLD = 30000; // 30 seconds

        if (flagAge > STALE_THRESHOLD) {
          console.warn(
            `[redirectToServiceauth] Redirect flag is stale (${Math.round(flagAge / 1000)}s old) - clearing and retrying`
          );
          localStorage.removeItem(this.SS_REDIRECT_IN_PROGRESS);
          localStorage.removeItem('pat.serviceauth.redirect.timestamp');
          // Fall through to set new flag and redirect
        } else {
          console.log(
            `[redirectToServiceauth] Recent redirect in progress (${Math.round(flagAge / 1000)}s old) - blocking duplicate`
          );
          return false; // Return false to indicate redirect was blocked
        }
      } else {
        // Flag exists but no timestamp - treat as stale
        console.warn(
          '[redirectToServiceauth] Redirect flag has no timestamp - clearing stale flag'
        );
        localStorage.removeItem(this.SS_REDIRECT_IN_PROGRESS);
        // Fall through to set new flag and redirect
      }
    }

    // Set flag immediately to block concurrent calls
    localStorage.setItem(this.SS_REDIRECT_IN_PROGRESS, 'true');
    localStorage.setItem(
      'pat.serviceauth.redirect.timestamp',
      Date.now().toString()
    );

    // Check if return path is already saved
    const existingReturnPath = localStorage.getItem(this.SS_RETURN);

    if (!existingReturnPath) {
      // Capture the path NOW before anything else can change it
      let returnPath = window.location.pathname;
      const currentSearch = window.location.search;

      if (currentSearch) {
        const params = new URLSearchParams(currentSearch);
        // Remove OAuth params if they exist
        params.delete('code');
        params.delete('state');
        const cleanParams = params.toString();
        returnPath += cleanParams ? `?${cleanParams}` : '';
      }

      localStorage.setItem(this.SS_RETURN, returnPath);
    }

    const url = await this.redirectToServiceauthUrl();
    window.location['assign'](url);
  }

  async redirectToServiceauthUrl(options) {
    const state = encode(createRandomString());
    const codeVerifier = createRandomString();
    const codeChallengeBuffer = await sha256(codeVerifier);
    const codeChallenge = bufferToBase64UrlEncoded(codeChallengeBuffer);

    var scopeParam = '';
    for (var i = 0; i < this.scopes.length; i++) {
      if (scopeParam.length > 0) {
        scopeParam += ' ';
      }
      scopeParam += this.scopes[i];
    }

    localStorage.setItem(this.SS_SA_CODE_VERIFIER, codeVerifier);
    localStorage.setItem(this.SS_SA_STATE, state);

    var url = this.serviceauthOauthUrl;
    url += '?response_type=code';
    url += '&client_id=' + encodeURIComponent(this.serviceauthId);
    url += '&redirect_uri=' + encodeURIComponent(this.serviceauthRedirectUri);
    url += '&state=' + encodeURIComponent(state);
    url += '&code_challenge_method=S256';
    url += '&code_challenge=' + codeChallenge;
    url += '&scope=' + encodeURIComponent(scopeParam);

    return url;
  }

  async handleRedirectCallback(url = window.location.href) {
    const queryStringFragments = url.split('?').slice(1);

    if (queryStringFragments.length === 0) {
      throw new Error('There are no query params available for parsing.');
    }

    const { state, code } = qs.parse(queryStringFragments.join(''));

    const storedState = localStorage.getItem(this.SS_SA_STATE);
    const codeVerifier = localStorage.getItem(this.SS_SA_CODE_VERIFIER);
    let returnTo = localStorage.getItem(this.SS_RETURN);

    if (state !== storedState) throw new Error('State values do not match.');

    // Clean returnTo path - remove any query params that might have been saved
    // This handles cases where OAuth params were in the original URL
    if (returnTo && returnTo.includes('?')) {
      const hasOAuthParams =
        returnTo.includes('code=') || returnTo.includes('state=');
      if (hasOAuthParams) {
        // Remove OAuth params but keep other query params
        const [path, queryString] = returnTo.split('?');
        const params = new URLSearchParams(queryString);
        params.delete('code');
        params.delete('state');
        const cleanParams = params.toString();
        returnTo = cleanParams ? `${path}?${cleanParams}` : path;
      }
    }

    // ASU version
    const response = await axios.post(
      this.serviceauthTokenUrl,
      qs.stringify({
        grant_type: 'authorization_code',
        code: code,
        redirect_uri: this.serviceauthRedirectUri,
        client_id: this.serviceauthId,
        client_secret: this.serviceauthSecret,
        code_verifier: codeVerifier,
      }),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );

    const { data } = response;

    localStorage.setItem(this.SA_JWT_TOKEN, data.access_token);
    localStorage.setItem(this.SS_JWT_REFRESH_TOKEN, data.refresh_token);
    var t = new Date(
      new Date().getTime() + this.TOKEN_EXPIRES_IN_SECONDS * 1000
    );
    localStorage.setItem(this.SS_JWT_EXPIRATION, t);

    //get app token
    try {
      const appTokenAndRoles = await this.getAppToken(data.access_token);
      localStorage.setItem(this.SS_JWT_TOKEN, appTokenAndRoles.access_token);

      // Decode ServiceAuth token to extract session expiration
      try {
        const decodedSAToken = decode(data.access_token);

        if (decodedSAToken?.exp) {
          // exp is in seconds since epoch, convert to milliseconds
          const expirationDate = new Date(decodedSAToken.exp * 1000);
          localStorage.setItem(
            this.SS_SESSION_EXPIRATION,
            expirationDate.toISOString()
          );
        }
      } catch (decodeError) {
        console.error(
          '[handleRedirectCallback] Error decoding ServiceAuth token:',
          decodeError
        );
      }

      // Clear any refresh failure flag on successful login
      sessionStorage.removeItem('pat.refresh.failed');
    } catch (error) {
      console.error('[handleRedirectCallback] Error getting PAT app token:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });

      // If user is not authorized (401 Unauthorized or 403 Forbidden), redirect to unauthorized page
      if (
        error.response &&
        (error.response.status === 401 || error.response.status === 403)
      ) {
        console.log(
          '[handleRedirectCallback] User not authorized - redirecting to /unauthorized'
        );
        return { returnTo: '/unauthorized' };
      }
      // Re-throw other errors
      throw error;
    }

    // If user is logging in from login page, return to redirect URL
    if (returnTo.startsWith('/login')) returnTo = '/';

    // Clear redirect in progress flag and timestamp since we successfully authenticated
    localStorage.removeItem(this.SS_REDIRECT_IN_PROGRESS);
    localStorage.removeItem('pat.serviceauth.redirect.timestamp');

    return { returnTo };
  }

  async getAppToken(token) {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const api = `${process.env.REACT_APP_API_ENDPOINT}/pat-app-token`;

    const response = await axios.get(api, headers);
    const { data } = response;

    return data;
  }

  async checkSession() {
    // If redirect is already in progress, don't check session
    const redirecting = localStorage.getItem(this.SS_REDIRECT_IN_PROGRESS);
    if (redirecting === 'true') {
      return;
    }

    if (!localStorage.getItem(this.SS_JWT_TOKEN)) {
      return;
    }

    try {
      await this.getAccessToken();
    } catch (error) {
      console.log('[checkSession] Error when checking session:', error);
    }
  }

  async getAccessToken(returnSAToken = false) {
    const getRefreshedAccessToken = async () => {
      var refreshToken = localStorage.getItem(this.SS_JWT_REFRESH_TOKEN);

      var scopeParam = '';
      for (var i = 0; i < this.scopes.length; i++) {
        if (scopeParam.length > 0) {
          scopeParam += ' ';
        }
        scopeParam += this.scopes[i];
      }

      // Request to refresh access token
      try {
        const response = await axios.post(
          this.serviceauthTokenRefreshUrl,
          qs.stringify({
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
            client_id: this.serviceauthId,
            client_secret: this.serviceauthSecret,
            scope: scopeParam,
          }),
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          }
        );

        const { data } = response;

        var t = new Date(
          new Date().getTime() + this.TOKEN_EXPIRES_IN_SECONDS * 1000
        );

        localStorage.setItem(this.SS_JWT_EXPIRATION, t);

        // Decode refreshed ServiceAuth token to update session expiration
        try {
          const decodedSAToken = decode(data.access_token);
          if (decodedSAToken?.exp) {
            const expirationDate = new Date(decodedSAToken.exp * 1000);
            localStorage.setItem(
              this.SS_SESSION_EXPIRATION,
              expirationDate.toISOString()
            );

            const now = new Date();
            const minutesUntilExpiration = Math.floor(
              (expirationDate - now) / 1000 / 60
            );
            console.log(
              `[getRefreshedAccessToken] Session expiration updated: ${expirationDate.toLocaleString()}`
            );
            console.log(
              `[getRefreshedAccessToken] Time until session expires: ${minutesUntilExpiration} minutes`
            );
          }
        } catch (decodeError) {
          console.error(
            '[getRefreshedAccessToken] Error decoding refreshed ServiceAuth token:',
            decodeError
          );
        }

        //get app token
        try {
          const appTokenAndRoles = await this.getAppToken(data.access_token);
          localStorage.setItem(this.SA_JWT_TOKEN, data.access_token);
          localStorage.setItem(
            this.SS_JWT_TOKEN,
            appTokenAndRoles.access_token
          );

          if (returnSAToken) return data.access_token;

          return appTokenAndRoles.access_token;
        } catch (appTokenError) {
          console.error('[getAccessToken] Error refreshing PAT app token:', {
            status: appTokenError.response?.status,
            statusText: appTokenError.response?.statusText,
            data: appTokenError.response?.data,
            message: appTokenError.message,
          });

          // If user is no longer authorized (401 or 403), redirect to unauthorized page
          if (
            appTokenError.response &&
            (appTokenError.response.status === 401 ||
              appTokenError.response.status === 403)
          ) {
            console.log(
              '[getAccessToken] User no longer authorized - redirecting to /unauthorized'
            );
            window.location.href = '/unauthorized';
            return;
          }
          throw appTokenError;
        }
      } catch (error) {
        console.error('[getNewAppTokenAndRoles] Error during token refresh:', {
          errorType: error.constructor.name,
          message: error.message,
          responseStatus: error.response?.status,
          responseData: error.response?.data,
        });

        // If we detect REFRESH_TOKEN_EXPIRED or 401/403, redirect to PAT app
        if (
          error.response?.data?.message === 'REFRESH_TOKEN_EXPIRED' ||
          error.response?.status === 401 ||
          error.response?.status === 403
        ) {
          console.error(
            '[getNewAppTokenAndRoles] Detected expired/unauthorized refresh token, redirecting to PAT app'
          );
          this.goToPATApp(false);
        }
        throw error;
      }
    };

    try {
      let token;

      if (returnSAToken) token = localStorage.getItem(this.SA_JWT_TOKEN);
      else token = localStorage.getItem(this.SS_JWT_TOKEN);

      if (token) {
        const currentDate = new Date();
        const tokenExpirationDate = new Date(
          localStorage.getItem(this.SS_JWT_EXPIRATION)
        );

        if (currentDate > tokenExpirationDate) {
          // If a refresh is already in progress, wait for it instead of starting a new one
          if (refreshInProgress) {
            console.log(
              '[ServiceAuthClient] Refresh already in progress - waiting for it to complete'
            );
            try {
              token = await refreshInProgress;
              return token;
            } catch (error) {
              // The first refresh failed, so throw the same error
              throw error;
            }
          }

          // Check if refresh already failed once in this session
          const refreshFailed = sessionStorage.getItem('pat.refresh.failed');

          if (refreshFailed) {
            // Second strike - clear everything and redirect
            console.log(
              '[ServiceAuthClient] Second refresh failure detected - redirecting to login'
            );
            this.clearAllAuthData();
            this.redirectToServiceauth();
            return null;
          }

          try {
            // Start the refresh and store the promise so other calls can wait for it
            refreshInProgress = getRefreshedAccessToken();
            token = await refreshInProgress;

            // Success - clear the promise and any failure flag
            refreshInProgress = null;
            sessionStorage.removeItem('pat.refresh.failed');
          } catch (error) {
            // Clear the promise so future calls can try again
            refreshInProgress = null;

            // First strike - set flag but don't redirect yet
            console.log(
              '[ServiceAuthClient] First refresh failure - setting flag'
            );
            sessionStorage.setItem('pat.refresh.failed', 'true');
            throw error; // Let the calling code handle it
          }
        }

        return token;
      } else this.redirectToServiceauth();
    } catch (e) {
      console.log('Error getting access token: ' + e);
      throw e; // Re-throw to prevent silent failures
    }
  }

  saveFormDataToSession(data, key = 'pat.saved.form.data') {
    try {
      // Include user identity for verification on restoration
      const user = this.getUserSync();
      const dataWithIdentity = {
        ...data,
        _savedByUser: user,
        _savedAt: new Date().toISOString(),
      };

      sessionStorage.setItem(key, JSON.stringify(dataWithIdentity));
      sessionStorage.setItem(
        'pat.saved.form.timestamp',
        new Date().toISOString()
      );
      // Clear the restoration flag so data can be restored after next login
      sessionStorage.removeItem('pat.data.restored');
      console.log(
        '[ServiceAuthClient] Form data saved to session storage with user identity:',
        user
      );
    } catch (error) {
      console.error('[ServiceAuthClient] Error saving form data:', error);
    }
  }

  getFormDataFromSession(key = 'pat.saved.form.data') {
    try {
      const data = sessionStorage.getItem(key);
      const timestamp = sessionStorage.getItem('pat.saved.form.timestamp');
      if (data) {
        const parsedData = JSON.parse(data);
        const currentUser = this.getUserSync();

        // Verify the data belongs to the current user
        if (
          parsedData._savedByUser &&
          parsedData._savedByUser !== currentUser
        ) {
          console.warn(
            '[ServiceAuthClient] Saved form data belongs to different user',
            '\n  Saved by:',
            parsedData._savedByUser,
            '\n  Current user:',
            currentUser,
            '\n  Clearing stale data to prevent cross-user contamination'
          );
          sessionStorage.removeItem(key);
          sessionStorage.removeItem('pat.saved.form.timestamp');
          return null;
        }

        console.log(
          '[ServiceAuthClient] Retrieved saved form data from:',
          timestamp,
          'for user:',
          parsedData._savedByUser
        );

        // Clear after retrieval
        sessionStorage.removeItem(key);
        sessionStorage.removeItem('pat.saved.form.timestamp');

        // Remove internal tracking fields before returning
        const { _savedByUser, _savedAt, ...cleanData } = parsedData;
        return cleanData;
      }
    } catch (error) {
      console.error(
        '[ServiceAuthClient] Error retrieving saved form data:',
        error
      );
    }
    return null;
  }

  // Get remaining session time in minutes
  getSessionTimeRemaining() {
    try {
      const sessionExpiration = localStorage.getItem(
        this.SS_SESSION_EXPIRATION
      );
      if (!sessionExpiration) {
        console.log('[getSessionTimeRemaining] No session expiration found');
        return null;
      }

      const expirationDate = new Date(sessionExpiration);
      const now = new Date();
      const millisecondsRemaining = expirationDate - now;
      const minutesRemaining = Math.floor(millisecondsRemaining / 1000 / 60);

      console.log(
        `[getSessionTimeRemaining] Session expires at: ${expirationDate.toLocaleString()}`
      );
      console.log(
        `[getSessionTimeRemaining] Minutes remaining: ${minutesRemaining}`
      );

      return {
        expirationDate,
        minutesRemaining,
        secondsRemaining: Math.floor(millisecondsRemaining / 1000),
        isExpired: millisecondsRemaining <= 0,
      };
    } catch (error) {
      console.error(
        '[getSessionTimeRemaining] Error calculating session time:',
        error
      );
      return null;
    }
  }

  /**
   * Clears all authentication data from storage
   * Use this when a session expires or user logs out
   *
   * NOTE: Does NOT clear sessionStorage form data (template-form-data, courselist-form-data, etc.)
   * Those items are preserved so users can restore their work after re-authenticating.
   * Components handle their own sessionStorage cleanup after successful restoration.
   */
  clearAllAuthData() {
    console.log('[ServiceAuthClient] Clearing all auth data from localStorage');

    // Clear all localStorage auth items
    localStorage.removeItem(this.SS_JWT_TOKEN);
    localStorage.removeItem(this.SA_JWT_TOKEN);
    localStorage.removeItem(this.SS_JWT_REFRESH_TOKEN);
    localStorage.removeItem(this.SS_JWT_EXPIRATION);
    localStorage.removeItem(this.SS_SESSION_EXPIRATION);
    localStorage.removeItem(this.SS_REDIRECT_IN_PROGRESS);
    localStorage.removeItem('pat.serviceauth.redirect.timestamp');
    localStorage.removeItem(this.SS_RETURN);
    localStorage.removeItem(this.SS_SA_STATE);
    localStorage.removeItem(this.SS_SA_CODE_VERIFIER);

    // Clear all user info items
    localStorage.removeItem(this.USER_EMPLID);
    localStorage.removeItem(this.USER_ROLE);
    localStorage.removeItem(this.USER_COLLEGE);
    localStorage.removeItem(this.USER_DEPARTMENT);
    localStorage.removeItem(this.USER_PERMISSIONS);
    localStorage.removeItem(this.USER_DETAILS);
    localStorage.removeItem(this.USER_EMAIL_PREFERENCE);

    // Clear refresh failure tracking flag
    // (This is safe to clear - it just prevents infinite refresh loops)
    sessionStorage.removeItem('pat.refresh.failed');

    // NOTE: We deliberately DO NOT clear form data from sessionStorage:
    // - template-form-data, template-form-should-restore
    // - courselist-form-data, courselist-modal-reopen
    // - unsavedCourses, pat.saved.form.data
    // These are preserved so users can restore their work after re-authentication.
    // Each component cleans up its own sessionStorage after successful restoration.

    console.log(
      '[ServiceAuthClient] Auth data cleared (sessionStorage form data preserved)'
    );
  }

  logout() {
    this.clearAllAuthData();
  }

  /**
   * Get current user synchronously from token
   * Used internally for user identity verification
   */
  getUserSync() {
    try {
      const token = localStorage.getItem(this.SS_JWT_TOKEN);
      if (!token) return null;

      const decodedToken = decode(token);
      return (
        (decodedToken && decodedToken.email) ||
        (decodedToken && decodedToken.sub) ||
        null
      );
    } catch (error) {
      console.error('[ServiceAuthClient] Error decoding token:', error);
      return null;
    }
  }

  async getUser() {
    const token = localStorage.getItem(this.SS_JWT_TOKEN);
    var decodedToken = decode(token);

    if (decodedToken && !localStorage.getItem(this.USER_ROLE)) {
      localStorage.setItem(this.USER_ROLE, decodedToken.roles);
      localStorage.setItem(this.USER_COLLEGE, decodedToken.college);
      localStorage.setItem(this.USER_DEPARTMENT, decodedToken.department);
      localStorage.setItem(this.USER_DETAILS, decodedToken.details);
      localStorage.setItem(this.USER_PERMISSIONS, decodedToken.scope);
      localStorage.setItem(
        this.USER_EMAIL_PREFERENCE,
        decodedToken.sendAllEmails
      );
    }

    return (
      (decodedToken && decodedToken.email) || (decodedToken && decodedToken.sub)
    );
  }
}

export default ServiceAuthClient;
